import { observable, flow, action, makeObservable, computed } from 'decorators';
import { when } from 'mobx';
import api from 'services/api';
import auth from 'services/auth';
import log from 'services/log';
import userService from 'services/data/user';
import Form, { isRequired, checkIf, maxLength, customError } from 'components/form';
import { insurerFields } from 'services/data/insurers';

export const supportedClearinghouses = [
  'kalix',
  'availity',
  'claim_md',
  'office_ally',
  'trizetto'
];

export const clearingHouseName = {
  kalix: 'Kalix',
  availity: 'Availity',
  claim_md: 'Claim MD',
  office_ally: 'Office Ally',
  trizetto: 'Trizetto'
};

export const clearingHouseLoginLink = {
  kalix: 'https://www.kalixhealth.com',
  availity: 'https://apps.availity.com/availity/web/public.elegant.login',
  claim_md: 'https://www.claim.md/',
  office_ally: 'https://www.officeally.com/slogin.aspx',
  trizetto: 'https://mytools.gatewayedi.com/LogOn'
};

export const clearingHouseDescriptions = {
  kalix: 'Kalix\'s managed insurance clearinghouse for seamless claim submission, tracking, and management, priced at $15/month per billing Tax ID. Includes unlimited claims, payers, and rendering providers.',
  availity: 'A popular clearinghouse providing free claim submission to participating insurers, with a $35/month subscription for submitting claims to non-participating insurers.',
  claim_md: 'A paid clearinghouse offering volume-based plans starting at $25/month.',
  office_ally: 'A flexible clearinghouse offering free claim submission if less than 50% of claims are sent to non-participating insurers. For accounts exceeding 50% non-par claims, a $44.95/month fee applies per unique Tax ID and rendering NPI combination.',
  trizetto: 'A comprehensive, paid clearinghouse tailored for larger practices, offering seamless claim submission and credentialing services to enhance efficiency and scalability.'
};

export default class ClearinghouseStore {
  @observable clearing = null;
  @observable form = null;
  @observable canCancel = false;
  @observable _isLoading = false;
  @observable externalIdForm = null;
  @observable externalIdPopup = null;

  constructor() {
    makeObservable(this);

    this.searchForm = new Form({
      fields: [
        {
          name: 'clearing',
          label: 'Clearinghouse',
          type: 'select',
          value: 'kalix',
          extra: {
            options: supportedClearinghouses.map(c => ({ name: clearingHouseName[c], value: c }))
          }
        },
        {
          name: 'payers',
          label: 'Search Payers',
          type: 'selectasync',
          extra: {
            calcFields: (f, form) => insurerFields({ excludeSaved: true, allowAllClearing: true, clearing: form.$('clearing').value })
          }
        }
      ]
    });
  }

  dispose() {}

  @computed get isLoading() {
    return this._isLoading || userService.clearinghouse.isLoading || this.status == null;
  }

  @computed get status() {
    return userService.clearinghouse.value;
  }

  @computed get needsTaxIds() {
    const ps = userService.postalAddresses;
    return !ps.isLoading && ps.value.length === 0;
  }

  @action startConnect(clearing) {
    this.clearing = clearing;
    this.canCancel = false;

    // Trizetto uses the Site ID
    const details = this.status?.clearing.find(ii => ii.clearing === clearing);
    if (clearing === 'kalix') {
      const currentTaxIds = details?.billingTaxIds || [];
      this.canCancel = currentTaxIds.length > 0;
      this.form = new Form({
        fields: [
          {
            name: 'taxIds',
            label: 'Saved Billing Provider Tax ID/s',
            type: 'select',
            value: currentTaxIds,
            extra: {
              multiple: true,
              calcFields: () => {
                const pos = userService.postalAddresses;
                const ids = new Set(pos.value.map(p => p.taxId));

                // Make sure we include any previous taxIDs
                if (details?.billingTaxIds) {
                  details.billingTaxIds.forEach(b => ids.add(b));
                }

                return {
                  options: [ ...ids ].filter(id => id).map(id => ({ value: id, name: id })),
                  isLoading: pos.isLoading
                };
              }
            }
          }
        ]
      });
    } else {
      const isTrizetto = clearing === 'trizetto';
      this.form = new Form({
        fields: [
          {
            name: 'userName',
            label: isTrizetto ? 'Site ID' : 'Username',
            disabled: isTrizetto,
            value: isTrizetto ? details?.externalId : undefined,
            validators: [ isRequired ]
          },
          {
            name: 'password',
            label: 'Password',
            type: 'password',
            validators: [ isRequired ]
          }
        ]
      });
    }
  }

  @action cancel() {
    this.form = null;
    this.clearing = null;
  }

  @flow *toggleEnabled(clearing) {
    this._isLoading = true;
    try {
      const isEnabled = !this.status?.clearing.find(ii => ii.clearing === clearing)?.enabled;

      // Trizetto and Claim MD need additional account number (external ID in Kalix)
      let externalId = '';
      if (isEnabled && (clearing === 'trizetto' || clearing === 'claim_md')) {
        const isTrizetto = clearing === 'trizetto';
        this.externalIdForm = new Form({
          fields: [
            {
              name: 'externalId',
              label: isTrizetto ? 'Site ID' : 'Account Number',
              validators: [ isRequired, checkIf(() => isTrizetto, [ customError(maxLength(4, { min: 4 }), 'Expecting 4-digit Site ID') ]) ]
            }
          ]
        });
        this.externalIdPopup = clearing;
        yield when(() => !this.externalIdPopup);
        if (this.externalIdForm == null) {
          this._isLoading = false;
          return;
        }

        externalId = this.externalIdForm.$('externalId').value;
        this.externalIdForm = null;
      }

      yield api.post(`clearinghouse/enabled?clearing=${clearing}&isEnabled=${isEnabled}&externalId=${externalId}`);
      yield userService.clearinghouse.waitForValue(true);
    } catch (err) {
      log.catchAndNotify(err);
    }
    this._isLoading = false;
  }

  @action closeExternalId() {
    this.externalIdForm = null;
    this.externalIdPopup = null;
  }

  @flow *submitExternalId(e) {
    yield this.externalIdForm.submitPromise(e);
    this.externalIdPopup = null;
  }

  @flow *connect(e) {
    yield this.form.submitPromise(e);
    const data = this.form.values();
    data.clearing = this.clearing;
    yield api.post('clearinghouse/connect', data);

    this.cancel();
    yield userService.clearinghouse.waitForValue(true);
  }

  @flow *revoke(clearing) {
    this._isLoading = true;
    try {
      yield api.delete(`clearinghouse?clearing=${clearing}`);
      yield userService.clearinghouse.waitForValue(true);
    } catch (err) {
      log.catchAndNotify(err);
    }
    this._isLoading = false;
  }

  saveBillIds() {
    const { taxIds } = this.form.values();
    return this._saveBillIds(taxIds);
  }

  cancelKalix() {
    return this._saveBillIds([]);
  }

  @flow *_saveBillIds(taxIds) {
    yield api.post('clearinghouse/kalix/taxids', { taxIds });

    this.cancel();
    yield auth.retry(true);
    yield userService.clearinghouse.waitForValue(true);
  }
}